import { format } from 'date-fns';

// Display labels for the payload fields Admin-Api writes into SubscriptionAuditLog
// OldValue/NewValue. Unlisted keys fall back to a humanized key name.
const FIELD_LABELS = {
  SubscriptionTerm: 'Subscription Term',
  StartDate: 'Start Date',
  EndDate: 'End Date',
  Year1Cost: 'Year 1 Cost',
  Year2Cost: 'Year 2 Cost',
  Year3Cost: 'Year 3 Cost',
  VatPercentApplied: 'VAT',
  Discount: 'Discount',
  Product: 'Product',
  IsChargeable: 'Chargeable',
  ProRatedAmount: 'Pro-rated Amount',
  InvoiceId: 'Invoice',
  Status: 'Status',
  TotalAmount: 'Total Amount',
  SubTotal: 'Subtotal',
  OriginalSubscriptionAmount: 'Original Subscription Amount',
  AccessGranted: 'Access Granted',
  CanToggleAtRenewal: 'Chargeable at Renewal',
};

const MONEY_KEYS = new Set([
  'Year1Cost',
  'Year2Cost',
  'Year3Cost',
  'Discount',
  'ProRatedAmount',
  'TotalAmount',
  'SubTotal',
  'OriginalSubscriptionAmount',
]);

const ISO_DATE_VALUE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

export const humanizeKey = (key) => String(key).replace(/([a-z0-9])([A-Z])/g, '$1 $2');

export const fieldLabel = (key) => FIELD_LABELS[key] || humanizeKey(key);

export const formatFieldValue = (key, value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (key === 'SubscriptionTerm') {
    const years = Number(value);
    return Number.isFinite(years) ? `${years} year${years === 1 ? '' : 's'}` : String(value);
  }
  if (key === 'VatPercentApplied') return `${value}%`;
  if (key === 'InvoiceId') return `#${value}`;
  if (MONEY_KEYS.has(key) || /(Cost|Amount)$/.test(key)) {
    const amount = Number(value);
    if (Number.isFinite(amount)) return `£${amount.toFixed(2)}`;
  }
  if (typeof value === 'string' && ISO_DATE_VALUE.test(value)) {
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) return format(date, 'MMM dd, yyyy');
  }
  return String(value);
};

// Parses an audit OldValue/NewValue payload; only plain JSON objects are usable
// for field-level rendering, anything else is treated as free text.
export const parseAuditValue = (raw) => {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) return parsed;
    return null;
  } catch {
    return null;
  }
};

// Field-by-field changes between two payloads. Only keys present in the new
// payload are compared — entries like InvoiceVoided intentionally shrink the
// payload, and "removed" keys there are noise, not changes.
export const diffAuditFields = (oldObj, newObj) =>
  Object.keys(newObj)
    .map((key) => ({
      key,
      label: fieldLabel(key),
      from: formatFieldValue(key, oldObj[key]),
      to: formatFieldValue(key, newObj[key]),
    }))
    .filter((change) => change.from !== change.to);

// Headline + supporting facts for a single-sided payload (e.g. ProductAdded).
export const summarizeAuditFields = (obj) => {
  let headlineKey = null;
  let headline = null;
  if (obj.Product) {
    headlineKey = 'Product';
    headline = String(obj.Product);
  } else if (obj.InvoiceId !== undefined && obj.InvoiceId !== null) {
    headlineKey = 'InvoiceId';
    headline = `Invoice #${obj.InvoiceId}`;
  }
  const facts = Object.keys(obj)
    .filter((key) => key !== headlineKey)
    .map((key) => ({ key, label: fieldLabel(key), value: formatFieldValue(key, obj[key]) }));
  return { headline, facts };
};
